import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { GitBranch, Plus, ArrowRight, Pencil, Trash2, Zap, Clock, Bell } from 'lucide-react';

const LEAVE_TYPES = ['sick_leave', 'casual_leave', 'vacation', 'maternity_leave', 'paternity_leave', 'unpaid_leave', 'earned_leave', 'compensatory'];
const ROLES = [
  { value: 'hr', label: 'HR' },
  { value: 'school_admin', label: 'Principal / Admin' },
];
const typeLabels = { sick_leave: 'Sick Leave', casual_leave: 'Casual Leave', vacation: 'Vacation', maternity_leave: 'Maternity Leave', paternity_leave: 'Paternity Leave', unpaid_leave: 'Unpaid Leave', earned_leave: 'Earned Leave', compensatory: 'Compensatory Off' };

export default function WorkflowConfig() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    leave_type: '', approval_chain: ['hr'], auto_approve: false,
    auto_approve_max_days: 0, escalation_days: 3, notify_roles: []
  });

  const fetchWorkflows = async () => {
    try { const res = await api.get('/workflows'); setWorkflows(res.data.data.workflows); }
    catch { toast.error('Failed to load workflows'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchWorkflows(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await api.patch(`/workflows/${editing}`, form); toast.success('Workflow updated'); }
      else { await api.post('/workflows', form); toast.success('Workflow created'); }
      setShowDialog(false);
      setEditing(null);
      fetchWorkflows();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleEdit = (wf) => {
    setForm({ leave_type: wf.leave_type, approval_chain: wf.approval_chain, auto_approve: wf.auto_approve, auto_approve_max_days: wf.auto_approve_max_days, escalation_days: wf.escalation_days, notify_roles: wf.notify_roles || [] });
    setEditing(wf.workflow_id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this workflow?')) return;
    try { await api.delete(`/workflows/${id}`); toast.success('Deleted'); fetchWorkflows(); }
    catch { toast.error('Failed to delete'); }
  };

  const openNew = () => {
    setForm({ leave_type: '', approval_chain: ['hr'], auto_approve: false, auto_approve_max_days: 0, escalation_days: 3, notify_roles: [] });
    setEditing(null);
    setShowDialog(true);
  };

  const addChainStep = () => setForm({ ...form, approval_chain: [...form.approval_chain, 'school_admin'] });
  const removeChainStep = (idx) => setForm({ ...form, approval_chain: form.approval_chain.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-6" data-testid="workflow-config">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-teal-50 flex items-center justify-center">
            <GitBranch className="h-5 w-5 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Approval Workflows</h1>
            <p className="text-sm text-slate-500">Configure leave approval chains per leave type</p>
          </div>
        </div>
        <Button className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl" onClick={openNew} data-testid="add-workflow-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Workflow
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" /></div>
      ) : workflows.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <GitBranch className="h-12 w-12 text-white/10 mx-auto mb-4" />
          <p className="text-slate-500 mb-2">No workflows configured</p>
          <p className="text-xs text-slate-300">Default: all leaves require admin/HR approval</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="workflow-grid">
          {workflows.map((wf) => (
            <div key={wf.workflow_id} className="glass-card p-5" data-testid={`workflow-${wf.workflow_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-base font-semibold text-slate-800">{typeLabels[wf.leave_type] || wf.leave_type}</h3>
                  {wf.auto_approve && (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-600">
                      <Zap className="h-2.5 w-2.5" /> Auto-approve up to {wf.auto_approve_max_days} days
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-teal-600" onClick={() => handleEdit(wf)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-400" onClick={() => handleDelete(wf.workflow_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>

              {/* Approval Chain Visual */}
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <span className="px-2.5 py-1 rounded-lg bg-blue-500/15 text-blue-400 text-xs font-semibold">Employee</span>
                {wf.approval_chain.map((role, i) => (
                  <React.Fragment key={i}>
                    <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${role === 'hr' ? 'bg-amber-50 text-amber-600' : 'bg-teal-50 text-teal-600'}`}>
                      {role === 'hr' ? 'HR' : 'Principal'}
                    </span>
                  </React.Fragment>
                ))}
                <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-600 text-xs font-semibold">Approved</span>
              </div>

              <div className="flex items-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Escalate after {wf.escalation_days}d</span>
                {wf.notify_roles?.length > 0 && <span className="flex items-center gap-1"><Bell className="h-3 w-3" /> Notify: {wf.notify_roles.join(', ')}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#1a2332] border-slate-200 text-white max-w-lg" data-testid="workflow-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-800">{editing ? 'Edit' : 'Create'} Workflow</DialogTitle>
            <DialogDescription className="text-slate-500">Define approval chain for a leave type</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5 py-2">
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs">Leave Type</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })} disabled={!!editing}>
                <SelectTrigger className="bg-white border-slate-200 text-white rounded-xl"><SelectValue placeholder="Select leave type" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800">
                  {LEAVE_TYPES.map(lt => <SelectItem key={lt} value={lt}>{typeLabels[lt] || lt}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label className="text-slate-600 text-xs">Approval Chain</Label>
              <div className="space-y-2">
                {form.approval_chain.map((role, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-16">Step {idx + 1}</span>
                    <Select value={role} onValueChange={(v) => { const arr = [...form.approval_chain]; arr[idx] = v; setForm({ ...form, approval_chain: arr }); }}>
                      <SelectTrigger className="bg-white border-slate-200 text-white rounded-lg flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200 text-slate-800">
                        {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {form.approval_chain.length > 1 && (
                      <Button size="sm" type="button" variant="ghost" className="text-rose-600 h-8 px-2" onClick={() => removeChainStep(idx)}>X</Button>
                    )}
                  </div>
                ))}
                <Button size="sm" type="button" variant="outline" className="border-slate-200 text-slate-500 rounded-lg" onClick={addChainStep}>+ Add Step</Button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl bg-white border border-slate-200">
                <div>
                  <p className="text-sm text-slate-600">Auto-Approve</p>
                  <p className="text-[10px] text-slate-400">Skip approval for short leaves</p>
                </div>
                <Switch checked={form.auto_approve} onCheckedChange={(v) => setForm({ ...form, auto_approve: v })} />
              </div>
              {form.auto_approve && (
                <div className="space-y-2 pl-4">
                  <Label className="text-slate-600 text-xs">Max Days for Auto-Approve</Label>
                  <Input type="number" min="1" max="10" value={form.auto_approve_max_days} onChange={(e) => setForm({ ...form, auto_approve_max_days: parseInt(e.target.value) || 0 })} className="bg-white border-slate-200 text-white rounded-lg" />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600 text-xs">Escalation Days</Label>
              <Input type="number" min="1" max="30" value={form.escalation_days} onChange={(e) => setForm({ ...form, escalation_days: parseInt(e.target.value) || 3 })} className="bg-white border-slate-200 text-white rounded-lg" />
              <p className="text-[10px] text-slate-300">Auto-escalate if not acted upon within this many days</p>
            </div>

            <Button type="submit" className="w-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update Workflow' : 'Create Workflow'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
